import axios from "axios";
import { createContext, useContext, useEffect, useState, useCallback } from "react";

export const AuthContext = createContext();

const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;

if (!USER_BACKEND_API_BASE_URL) {
  console.error("La variable d'environnement REACT_APP_USER_BACKEND_API_URL n'est pas définie !");
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialCheckComplete, setInitialCheckComplete] = useState(false);

  // Fonction pour vérifier l'authentification
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      setInitialCheckComplete(true);
      return;
    }

    try {
      const usersMeUrl = `${USER_BACKEND_API_BASE_URL}/users/me`;
      const response = await axios.get(usersMeUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUser(response.data);
      setIsAuthenticated(true);
      setError(null);
    } catch (err) {
      console.error("Erreur d'authentification:", err);
      setIsAuthenticated(false);
      setUser(null);
      setError("Session expirée");
      
      // Nettoyer le token invalide
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
      setInitialCheckComplete(true);
    }
  }, []);

  // Fonction de login qui met à jour le contexte
  const login = useCallback(async (token, userData = null) => {
    localStorage.setItem("access_token", token);
    
    if (userData) {
      // Si les données utilisateur sont déjà disponibles
      setUser(userData);
      setIsAuthenticated(true);
      setError(null);
    } else {
      // Sinon, récupérer les données utilisateur
      await checkAuth();
    }
  }, [checkAuth]);

  // Fonction de déconnexion améliorée
  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    setInitialCheckComplete(true);
  }, []);

  // Fonction pour rafraîchir les données utilisateur
  const refreshUser = useCallback(async () => {
    if (isAuthenticated) {
      await checkAuth();
    }
  }, [isAuthenticated, checkAuth]);

  // Vérification initiale de l'authentification
  useEffect(() => {
    if (!initialCheckComplete) {
      checkAuth();
    }
  }, [checkAuth, initialCheckComplete]);

  // Vérification si le profil est incomplet
  const isProfileIncomplete = user && (!user.prenom || !user.nom);

  const contextValue = {
    user,
    isAuthenticated,
    loading,
    error,
    isProfileIncomplete,
    initialCheckComplete,
    login,
    logout,
    refreshUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider");
  }
  return ctx;
};